import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Copy, Check, ShieldCheck, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { primaryHomeForRoles } from "@/lib/auth";

// Curated seed accounts. Cada botón hace LOGIN INMEDIATO (no solo prefill).
// Cubre los 7 roles reales del DB: superadmin_dcc, staff_dcc, instructor,
// partner_admin, alumno, reseller, influencer.
const ACCOUNTS = [
  { icon: "🛡️", label: "Superadmin DCC", subtitle: "Acceso total · /app/superadmin",
    email: "superadmin@dcc.mx", needs2fa: true },
  { icon: "👔", label: "Staff DCC", subtitle: "Operaciones · /app/staff",
    email: "staff@dcc.mx", needs2fa: true },
  { icon: "🎓", label: "Instructor DCC", subtitle: "Mentor · /app/instructor",
    email: "instructor@dcc.mx", needs2fa: false },
  { icon: "🏦", label: "Partner Admin · Banco Alfa", subtitle: "Kapital · /app/partner",
    email: "admin@banco-alfa.mx", needs2fa: true },
  { icon: "🏦", label: "Partner Admin · Demo Bank", subtitle: "Demo Bank · /app/partner",
    email: "admin@demo-bank.mx", needs2fa: true },
  { icon: "🏢", label: "Partner Admin · Trafalgar (Multi-tenant)", subtitle: "2 tenants · Sofipo + CB · /app/partner",
    email: "rh@trafalgar.mx", needs2fa: true, badge: "MULTI-TENANT" },
  { icon: "👤", label: "Alumno · Banco Alfa", subtitle: "Estudiante · /app/alumno",
    email: "alumno@banco-alfa.mx", needs2fa: false },
  { icon: "👤", label: "Alumno · Demo Bank", subtitle: "Estudiante · /app/alumno",
    email: "alumno2@demo-bank.mx", needs2fa: false },
  { icon: "💼", label: "Reseller", subtitle: "Reventa afiliada · /app/affiliate",
    email: "reseller@capital-training.mx", needs2fa: false },
  { icon: "📣", label: "Influencer", subtitle: "Creator afiliada · /app/affiliate",
    email: "influencer@laurariesgo.mx", needs2fa: false },
];

const DEMO_PASSWORD = "Demo2026!";

export default function DemoLoginButtons({ onPrefill }) {
  const [enabled, setEnabled] = useState(null);
  const [require2fa, setRequire2fa] = useState(true);
  const [copied, setCopied] = useState(false);
  const [loadingEmail, setLoadingEmail] = useState(null);
  const nav = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  useEffect(() => {
    api.get("/config/public")
      .then((r) => {
        setEnabled(!!r.data?.show_demo_credentials);
        setRequire2fa(r.data?.require_2fa !== false);
      })
      .catch(() => setEnabled(false));
  }, []);

  if (!enabled) return null;

  const copyPassword = async () => {
    let ok = false;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(DEMO_PASSWORD); ok = true;
      }
    } catch { ok = false; }
    if (!ok) {
      try {
        const ta = document.createElement("textarea");
        ta.value = DEMO_PASSWORD; ta.setAttribute("readonly", "");
        ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.select();
        ok = document.execCommand("copy");
        document.body.removeChild(ta);
      } catch { ok = false; }
    }
    if (ok) {
      setCopied(true);
      toast.success("Contraseña copiada");
      setTimeout(() => setCopied(false), 1400);
    } else {
      toast.error("No se pudo copiar");
    }
  };

  // Click → login inmediato. También llama onPrefill para actualizar el form
  // visible por si el usuario quiere refactorear los inputs después.
  const quickLogin = async (account) => {
    setLoadingEmail(account.email);
    onPrefill?.({ email: account.email, password: DEMO_PASSWORD });
    try {
      const { data } = await api.post("/auth/login", {
        email: account.email, password: DEMO_PASSWORD,
      });
      // 2FA setup requerido en primer acceso — pasamos QR
      if (data.requires_2fa_setup) {
        toast.info(`${account.label}: configura tu 2FA para continuar.`);
        nav("/setup-2fa", {
          state: {
            setup_token: data.setup_token,
            qr_data_url: data.qr_data_url,
            email: account.email,
          },
        });
        return;
      }
      // 2FA ya configurado — desde demo no lo pedimos (el user usaría el form)
      if (data.requires_2fa) {
        toast.error("Esta cuenta requiere código TOTP. Usa el formulario manual.");
        return;
      }
      login(data);
      toast.success(`Bienvenido, ${data.user.full_name}`);
      const params = new URLSearchParams(location.search);
      const nextParam = params.get("next");
      const safeNext = nextParam && nextParam.startsWith("/") && !nextParam.startsWith("//") ? nextParam : null;
      const to = safeNext || location.state?.from || primaryHomeForRoles(data.user.roles);
      nav(to);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoadingEmail(null);
    }
  };

  return (
    <div
      data-testid="demo-login-panel"
      className="mb-6 bg-[#F9FAFB] border border-gray-200 border-l-4 border-l-[#F26A21] rounded-lg p-4"
    >
      <div className="flex items-center gap-2">
        <span className="text-[10px] font-semibold uppercase tracking-widest text-[#F26A21]">
          Modo demo · click para entrar directo
        </span>
      </div>
      <p className="mt-1 text-sm font-bold text-[#0B0B0B]">Acceso rápido por rol</p>
      <div className="mt-3 grid grid-cols-1 gap-2" data-testid="demo-buttons-grid">
        {ACCOUNTS.map((a) => {
          const isLoading = loadingEmail === a.email;
          const isDisabled = loadingEmail && loadingEmail !== a.email;
          return (
            <button
              key={a.email}
              type="button"
              onClick={() => quickLogin(a)}
              disabled={isDisabled}
              data-testid={`demo-login-btn-${a.email}`}
              title={a.needs2fa ? "Requiere setup 2FA al primer inicio" : "Entra directo"}
              className="group flex items-center justify-between gap-3 text-left px-3 py-2.5 rounded-md bg-white border border-gray-200 hover:border-[#F26A21] hover:bg-[#F26A21]/5 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <span className="flex items-start gap-2.5 min-w-0 flex-1">
                <span className="text-base flex-shrink-0" aria-hidden="true">{a.icon}</span>
                <span className="min-w-0 flex-1">
                  <span className="block text-xs font-semibold text-[#0B0B0B] truncate">{a.label}</span>
                  <span className="block text-[10px] text-gray-500 truncate">{a.subtitle}</span>
                </span>
              </span>
              <span className="flex items-center gap-1.5 flex-shrink-0">
                {a.needs2fa && require2fa && (
                  <span
                    data-testid={`demo-2fa-badge-${a.email}`}
                    className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-widest text-[#F26A21] bg-[#F26A21]/10 border border-[#F26A21]/40 rounded px-1.5 py-0.5"
                  >
                    <ShieldCheck className="w-2.5 h-2.5" /> 2FA
                  </span>
                )}
                {isLoading
                  ? <Loader2 className="w-3.5 h-3.5 text-[#F26A21] animate-spin" />
                  : <ArrowRight className="w-3.5 h-3.5 text-gray-400 group-hover:text-[#F26A21] group-hover:translate-x-0.5 transition-all" />}
              </span>
            </button>
          );
        })}
      </div>
      <div className="mt-3 flex items-center justify-between text-xs text-gray-500">
        <span>
          Contraseña común: <code className="font-mono text-[#0B0B0B]">{DEMO_PASSWORD}</code>
        </span>
        <button
          type="button"
          onClick={copyPassword}
          data-testid="demo-copy-password-btn"
          className="inline-flex items-center gap-1 text-xs text-[#F26A21] hover:text-[#D9541A] font-semibold"
        >
          {copied ? <><Check className="w-3 h-3" /> Copiado</> : <><Copy className="w-3 h-3" /> Copiar</>}
        </button>
      </div>
    </div>
  );
}
